import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import AIJobProgressLoader, { FileAnalysisSubtitle } from 'components/ai/AIJobProgressLoader';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import useAIJobChannel from 'hooks/useAIJobChannel';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { useStageTimer } from 'hooks/useStageTimer';
import { updateJobbableCacheWithAIJob } from 'lib/aiJobHelpers';
import AIJob from 'models/AIJob';
import Summarization from 'models/Summarization';
import { summarizationQueryKeys } from 'services/api/summarizations';

enum Stage {
  ProcessingFiles = 'Processing files',
  IdentifyingPatientDemographics = 'Identifying patient demographics',
  GeneratingSummary = 'Generating summary',
  FinishingUp = 'Finishing up',
}

const STAGES = Array.from(Object.values(Stage));

type SummaryLoadingStateProps = {
  summarization: Summarization;
  onLoadingFinished: () => void;
};

export default function LoadingState({ summarization, onLoadingFinished }: SummaryLoadingStateProps) {
  const invalidateQueries = useInvalidateQueries();

  const { attachments, patientDemographicsJob, summaryJob } = summarization;

  const fileCount = attachments.length;
  const summaryJobUpdatedAt = summaryJob?.updatedAt;

  // Only start the timer when the summary job is awaiting (file processing + patient demographics are done)
  const timerReferenceDate = summaryJob?.isAwaiting ? summaryJobUpdatedAt : null;
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const stage = useMemo(() => {
    if (summaryJob?.isCompleted) {
      return STAGES.length + 1;
    }

    let currentStage: Stage;

    if (summaryJob?.isAwaiting) {
      if (onFinalStage) {
        currentStage = Stage.FinishingUp;
      } else {
        currentStage = Stage.GeneratingSummary;
      }
    } else if (patientDemographicsJob?.isAwaiting) {
      currentStage = Stage.IdentifyingPatientDemographics;
    } else if (patientDemographicsJob?.isPending) {
      currentStage = Stage.ProcessingFiles;
    } else {
      return 0;
    }

    return STAGES.indexOf(currentStage) + 1;
  }, [patientDemographicsJob, summaryJob, onFinalStage]);

  const count = useMemo(() => {
    return stage
      ? attachments.filter((attachment) => attachment.extractDataAIJob && !attachment.extractDataAIJob.isProcessing)
          .length
      : 0;
  }, [stage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (stage) {
      const timer = setTimeout(() => {
        setCompletedCount(count);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [stage, count]);

  const stages = useMemo(
    () =>
      STAGES.map((stage) => ({
        title: stage,
        ...(stage === Stage.ProcessingFiles
          ? {
              subtitle: `${completedCount} of ${fileCount} complete`,
              subtasks: [
                { title: 'Scanning document text', annotation: `${completedCount}/${fileCount}` },
                { title: 'Extracting key information', annotation: `${completedCount}/${fileCount}` },
                { title: 'Creating source citations', annotation: `${completedCount}/${fileCount}` },
              ],
            }
          : {}),
      })),
    [completedCount, fileCount]
  );

  // subscribe to Patient Demographics job without any notification tracking
  useAIJobChannel({
    params: {
      ai_job_id: patientDemographicsJob?.isProcessing ? patientDemographicsJob.id : undefined,
      attachable_model: 'Summarization',
    },
    onMessageReceived: (data) => {
      const tempJob = new AIJob(data);
      if (tempJob.isPending) return;

      if (tempJob.isCompleted) {
        invalidateQueries({ summarization: { id: summarization?.id } });
      } else {
        updateJobbableCacheWithAIJob(summarizationQueryKeys.show({ id: summarization?.id }), data);
      }
    },
  });

  return (
    <LoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={summarization} />
      ))}
      <CenteredContent>
        <AIJobProgressLoader
          title='Generating Summary'
          subtitle={<FileAnalysisSubtitle fileCount={fileCount} />}
          stages={stages}
          stage={stage}
          onLoadingFinished={onLoadingFinished}
        />
      </CenteredContent>
      <TimingExplainer jobType='summary' />
    </LoaderContainer>
  );
}

const LoaderContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
